import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map, startWith } from 'rxjs';
import { SkillQuestionTypeEnum, SkillQuestionTypeEnumOptions } from 'src/app/enums/skill-question-type.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Skill } from 'src/app/models/skill.model';
import {
  SkillQuestion,
  SkillQuestionBody,
  SkillQuestionRubricPayload,
  SkillQuestionRubricSuggestionResponse,
} from 'src/app/models/skill_question.model';
import { SkillQuestionRubricSuggestionDialogComponent } from 'src/app/components/main/employees/rrhh/skill-question-rubric-suggestion-dialog/skill-question-rubric-suggestion-dialog.component';
import { ApiService } from 'src/app/services';

const RUBRIC_AI_SUGGESTION_MIN_BODY_LENGTH = 30;

@Component({
  selector: 'app-skill-question-detail',
  templateUrl: './skill-question-detail.component.html',
  styleUrl: './skill-question-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class SkillQuestionDetailComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly mat_dialog = inject(MatDialog);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  readonly loading = signal(false);
  readonly loading_data = signal(false);
  readonly loading_rubric_suggestion = signal(false);
  readonly skills = signal<Skill[]>([]);
  readonly rubric_rules = signal<string[][]>([]);
  readonly rubric_cell_weight_percents = signal<number[][]>([]);
  readonly rubric_levels_count = signal(0);

  readonly skill_question_type_options = SkillQuestionTypeEnumOptions;

  readonly is_create_mode = toSignal(this.activated_route.data.pipe(map(route_data => route_data['mode'] === 'create')), {
    initialValue: false,
  });

  readonly skill_question_id = toSignal(
    this.activated_route.paramMap.pipe(
      map(param_map => {
        const raw_id = param_map.get('id');

        if (!raw_id) {
          return null;
        }

        const parsed_id = Number.parseInt(raw_id, 10);

        return Number.isNaN(parsed_id) ? null : parsed_id;
      })
    ),
    { initialValue: null as number | null }
  );

  readonly form = this.form_builder.group({
    type: [SkillQuestionTypeEnum.Text, Validators.required],
    body: ['', Validators.required],
    approximate_minutes: [null as number | null],
    answer_lines: [null as number | null],
    skill_ids: [[] as number[]],
    options: this.form_builder.array([]),
    rubric_dimensions: this.form_builder.array([]),
    rubric_levels: this.form_builder.array([]),
  });

  readonly question_type = toSignal(this.form.controls.type.valueChanges.pipe(startWith(this.form.controls.type.value)), {
    initialValue: SkillQuestionTypeEnum.Text,
  });

  readonly is_text_type = computed(() => this.question_type() === SkillQuestionTypeEnum.Text);

  readonly is_test_type = computed(() => !this.is_text_type());

  readonly question_body_length = toSignal(
    this.form.controls.body.valueChanges.pipe(
      startWith(this.form.controls.body.value),
      map(body => (body ?? '').trim().length)
    ),
    { initialValue: 0 }
  );

  readonly selected_skill_ids_count = toSignal(
    this.form.controls.skill_ids.valueChanges.pipe(
      startWith(this.form.controls.skill_ids.value),
      map(skill_ids => (skill_ids ?? []).length)
    ),
    { initialValue: 0 }
  );

  readonly rubric_weights_sum = signal(0);

  readonly rubric_weights_valid = computed(() => this.rubric_weights_sum() === 100);

  readonly can_suggest_rubric_with_ai = computed(
    () =>
      this.is_text_type() &&
      this.selected_skill_ids_count() > 0 &&
      this.question_body_length() >= RUBRIC_AI_SUGGESTION_MIN_BODY_LENGTH &&
      this.rubric_levels_count() > 2 &&
      !this.loading_rubric_suggestion() &&
      !this.loading() &&
      !this.loading_data()
  );

  readonly rubric_ai_suggestion_disabled_reason = computed((): string => {
    if (!this.is_text_type()) {
      return 'Solo disponible para preguntas de texto.';
    }

    if (this.loading_rubric_suggestion()) {
      return 'Generando sugerencia...';
    }

    if (this.loading() || this.loading_data()) {
      return 'Espera a que termine la carga o el guardado.';
    }

    if (this.selected_skill_ids_count() === 0) {
      return 'Debes seleccionar al menos una skill relacionada.';
    }

    if (this.question_body_length() < RUBRIC_AI_SUGGESTION_MIN_BODY_LENGTH) {
      return `El enunciado debe tener al menos ${RUBRIC_AI_SUGGESTION_MIN_BODY_LENGTH} caracteres (actual: ${this.question_body_length()}).`;
    }

    if (this.rubric_levels_count() <= 2) {
      return 'Debes definir más de 2 niveles en la rúbrica.';
    }

    return '';
  });

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  ngOnInit(): void {
    this.loadSkills();
    this.listenQuestionTypeChanges();
    this.listenRubricWeightChanges();

    if (this.is_create_mode()) {
      this.initializeDefaultRubricForTextQuestion();
    } else {
      const skill_question_id = this.skill_question_id();

      if (skill_question_id) {
        this.loadSkillQuestion(skill_question_id);
      }
    }
  }

  get options_form_array(): FormArray {
    return this.form.controls.options;
  }

  get rubric_dimensions_form_array(): FormArray {
    return this.form.controls.rubric_dimensions;
  }

  get rubric_levels_form_array(): FormArray {
    return this.form.controls.rubric_levels;
  }

  addRubricDimension(): void {
    const default_weight_percent = this.rubric_dimensions_form_array.length === 0 ? 100 : 0;

    this.rubric_dimensions_form_array.push(
      this.form_builder.group({
        name: ['', Validators.required],
        weight_percent: [default_weight_percent, [Validators.required, Validators.min(1), Validators.max(100)]],
      })
    );
    this.resizeRubricRulesMatrix();
    this.updateRubricWeightsSum();
    this.bumpRubricMatrixRevision();
  }

  removeRubricDimension(dimension_index: number): void {
    this.rubric_dimensions_form_array.removeAt(dimension_index);
    this.resizeRubricRulesMatrix();
    this.updateRubricWeightsSum();
    this.bumpRubricMatrixRevision();
  }

  addRubricLevel(): void {
    const default_weight_percent = this.rubric_levels_form_array.length === 0 ? 100 : 0;

    this.rubric_levels_form_array.push(
      this.form_builder.group({
        name: ['', Validators.required],
        weight_percent: [default_weight_percent, [Validators.required, Validators.min(0), Validators.max(100)]],
      })
    );
    this.resizeRubricRulesMatrix();
    this.bumpRubricMatrixRevision();
    this.updateRubricLevelsCount();
  }

  removeRubricLevel(level_index: number): void {
    this.rubric_levels_form_array.removeAt(level_index);
    this.resizeRubricRulesMatrix();
    this.bumpRubricMatrixRevision();
    this.updateRubricLevelsCount();
  }

  updateRubricRule(dimension_index: number, level_index: number, rules: string): void {
    this.rubric_rules.update(current_rules => {
      const next_rules = current_rules.map(row => [...row]);

      if (!next_rules[dimension_index]) {
        return next_rules;
      }

      next_rules[dimension_index][level_index] = rules;

      return next_rules;
    });
  }

  addOption(): void {
    this.options_form_array.push(
      this.form_builder.group({
        label: ['', Validators.required],
        is_correct: [false],
      })
    );
  }

  removeOption(option_index: number): void {
    this.options_form_array.removeAt(option_index);
  }

  save(): void {
    if (this.loading() || this.loading_data() || this.form.invalid) {
      return;
    }

    if (this.is_test_type() && this.options_form_array.length === 0) {
      this.snack_bar.open('Añade al menos una opción de respuesta.', undefined, { duration: 4000 });
      return;
    }

    if (this.is_text_type()) {
      const dimensions_count = this.rubric_dimensions_form_array.length;
      const levels_count = this.rubric_levels_form_array.length;

      if (dimensions_count === 0 || levels_count === 0) {
        this.snack_bar.open('La rúbrica debe tener al menos una dimensión y un nivel.', undefined, { duration: 4000 });
        return;
      }

      if (!this.rubric_weights_valid()) {
        this.snack_bar.open(`Los pesos de las dimensiones deben sumar 100 % (actual: ${this.rubric_weights_sum()} %).`, undefined, {
          duration: 5000,
        });
        return;
      }

      if (!this.areAllRubricRulesFilled()) {
        this.snack_bar.open('Completa las reglas de adjudicación en todas las celdas de la rúbrica.', undefined, {
          duration: 5000,
        });
        return;
      }
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: SkillQuestionBody = {
      type: raw.type as SkillQuestionTypeEnum,
      body: raw.body ?? '',
      approximate_minutes: raw.approximate_minutes,
      answer_lines: this.is_text_type() ? raw.answer_lines : null,
      skill_ids: raw.skill_ids ?? [],
      options: this.is_test_type() ? this.buildOptionsPayload() : [],
      rubric: this.is_text_type() ? this.buildRubricPayload() : null,
    };

    const skill_question_id = this.skill_question_id();
    const request$ =
      this.is_create_mode() || !skill_question_id
        ? this.api_service.createSkillQuestion(body)
        : this.api_service.updateSkillQuestion(skill_question_id, body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (): void => {
        this.snack_bar.open('Pregunta guardada', undefined, { duration: 3000 });
        void this.router.navigate(['/employees', 'rrhh', 'skill-questions']);
      },
      error: (err: { error?: { error?: string } }): void => {
        this.loading.set(false);
        const message = err?.error?.error ?? 'Error al guardar la pregunta.';
        this.snack_bar.open(message, undefined, { duration: 5000 });
      },
    });
  }

  cancel(): void {
    void this.router.navigate(['/employees', 'rrhh', 'skill-questions']);
  }

  openRubricAiSuggestionDialog(): void {
    if (!this.can_suggest_rubric_with_ai()) {
      return;
    }

    const dialog_ref = this.mat_dialog.open(SkillQuestionRubricSuggestionDialogComponent, {
      width: '560px',
      minWidth: '400px',
      data: {
        dialog_title: 'Sugerencia de rúbrica con IA',
        dialog_description:
          'La IA propondrá dimensiones y reglas de adjudicación para los niveles que ya has definido. Puedes añadir instrucciones extra sobre qué conceptos o criterios quieres priorizar.',
        dialog_cancel_button: 'Cancelar',
        dialog_confirm_button: 'Generar sugerencia',
      },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((additional_prompt: string | undefined): void => {
        if (additional_prompt === undefined) {
          return;
        }

        this.requestRubricAiSuggestion(additional_prompt);
      });
  }

  private requestRubricAiSuggestion(additional_prompt: string): void {
    const raw = this.form.getRawValue();
    const levels = this.rubric_levels_form_array.controls.map(level_control => {
      const level_value = level_control.getRawValue() as { name: string; weight_percent: number };

      return {
        name: level_value.name ?? '',
        weight_percent: level_value.weight_percent ?? 0,
      };
    });

    const dimensions = this.rubric_dimensions_form_array.controls.map(dimension_control => {
      const dimension_value = dimension_control.getRawValue() as { name: string; weight_percent: number };

      return {
        name: dimension_value.name ?? '',
        weight_percent: dimension_value.weight_percent ?? 0,
      };
    });

    const selected_skill_ids = new Set(raw.skill_ids ?? []);
    const skill_names = this.skills()
      .filter(skill => selected_skill_ids.has(skill.id))
      .map(skill => skill.name);

    this.loading_rubric_suggestion.set(true);

    this.api_service
      .suggestSkillQuestionRubric({
        body: raw.body ?? '',
        levels,
        dimensions,
        skill_names,
        additional_prompt: additional_prompt || null,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (suggestion: SkillQuestionRubricSuggestionResponse): void => {
          this.applyRubricSuggestion(suggestion);
          this.loading_rubric_suggestion.set(false);
          this.snack_bar.open('Sugerencia aplicada. Revisa la rúbrica antes de guardar.', undefined, {
            duration: 5000,
          });
          this.change_detector_ref.markForCheck();
        },
        error: (err: { error?: { error?: string } }): void => {
          this.loading_rubric_suggestion.set(false);
          const message = err?.error?.error ?? 'No se pudo generar la sugerencia de rúbrica.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private applyRubricSuggestion(suggestion: SkillQuestionRubricSuggestionResponse): void {
    this.form.patchValue({
      approximate_minutes: suggestion.approximate_minutes,
    });

    this.rubric_dimensions_form_array.clear();

    suggestion.dimensions.forEach(dimension => {
      this.rubric_dimensions_form_array.push(
        this.form_builder.group({
          name: [dimension.name, Validators.required],
          weight_percent: [dimension.weight_percent, [Validators.required, Validators.min(1), Validators.max(100)]],
        })
      );
    });

    this.resizeRubricRulesMatrix();

    suggestion.dimension_levels.forEach(dimension_level => {
      this.updateRubricRule(dimension_level.dimension_index, dimension_level.level_index, dimension_level.rules);
    });

    this.updateRubricWeightsSum();
    this.bumpRubricMatrixRevision();
  }

  private areAllRubricRulesFilled(): boolean {
    const rules_matrix = this.rubric_rules();

    return rules_matrix.every(dimension_rules => dimension_rules.every(cell_rules => cell_rules.trim().length > 0));
  }

  private buildOptionsPayload(): { label: string; is_correct: boolean; sort_order: number }[] {
    return this.options_form_array.controls.map((option_control, option_index) => {
      const option_value = option_control.getRawValue() as { label: string; is_correct: boolean };

      return {
        label: option_value.label ?? '',
        is_correct: option_value.is_correct ?? false,
        sort_order: option_index,
      };
    });
  }

  private buildRubricPayload(): SkillQuestionRubricPayload {
    const dimensions = this.rubric_dimensions_form_array.controls.map(dimension_control => {
      const dimension_value = dimension_control.getRawValue() as { name: string; weight_percent: number };

      return {
        name: dimension_value.name ?? '',
        weight_percent: dimension_value.weight_percent ?? 0,
      };
    });

    const levels = this.rubric_levels_form_array.controls.map((level_control, level_index) => {
      const level_value = level_control.getRawValue() as { name: string; weight_percent: number };

      return {
        name: level_value.name ?? '',
        weight_percent: level_value.weight_percent ?? 0,
        sort_order: level_index,
      };
    });

    const rules_matrix = this.rubric_rules();
    const dimension_levels: SkillQuestionRubricPayload['dimension_levels'] = [];

    dimensions.forEach((_, dimension_index) => {
      levels.forEach((_, level_index) => {
        dimension_levels.push({
          dimension_index,
          level_index,
          rules: rules_matrix[dimension_index]?.[level_index]?.trim() ?? '',
        });
      });
    });

    return { dimensions, levels, dimension_levels };
  }

  private resizeRubricRulesMatrix(): void {
    const dimension_count = this.rubric_dimensions_form_array.length;
    const level_count = this.rubric_levels_form_array.length;

    this.rubric_rules.update(current_rules => {
      const next_rules: string[][] = [];

      for (let dimension_index = 0; dimension_index < dimension_count; dimension_index += 1) {
        next_rules[dimension_index] = [];

        for (let level_index = 0; level_index < level_count; level_index += 1) {
          next_rules[dimension_index][level_index] = current_rules[dimension_index]?.[level_index] ?? '';
        }
      }

      return next_rules;
    });
  }

  private initializeDefaultRubricForTextQuestion(): void {
    if (this.is_text_type() && this.rubric_dimensions_form_array.length === 0) {
      this.addRubricDimension();
      this.addRubricLevel();
      this.updateRubricLevelsCount();
    }
  }

  private clearRubricForm(): void {
    this.rubric_dimensions_form_array.clear();
    this.rubric_levels_form_array.clear();
    this.rubric_rules.set([]);
    this.updateRubricWeightsSum();
    this.updateRubricLevelsCount();
    this.bumpRubricMatrixRevision();
  }

  private rebuildRubricCellWeightPercents(): void {
    const cell_weight_percents: number[][] = [];

    for (let dimension_index = 0; dimension_index < this.rubric_dimensions_form_array.length; dimension_index += 1) {
      cell_weight_percents[dimension_index] = [];

      const dimension_weight_percent = Number(this.rubric_dimensions_form_array.at(dimension_index)?.get('weight_percent')?.value ?? 0);

      for (let level_index = 0; level_index < this.rubric_levels_form_array.length; level_index += 1) {
        const level_weight_percent = Number(this.rubric_levels_form_array.at(level_index)?.get('weight_percent')?.value ?? 0);

        cell_weight_percents[dimension_index][level_index] = Math.round((dimension_weight_percent * level_weight_percent) / 100);
      }
    }

    this.rubric_cell_weight_percents.set(cell_weight_percents);
  }

  private bumpRubricMatrixRevision(): void {
    this.rebuildRubricCellWeightPercents();
  }

  private listenRubricWeightChanges(): void {
    this.rubric_dimensions_form_array.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => {
      this.updateRubricWeightsSum();
      this.bumpRubricMatrixRevision();
    });

    this.rubric_levels_form_array.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => {
      this.bumpRubricMatrixRevision();
    });
  }

  private updateRubricWeightsSum(): void {
    const weights_sum = this.rubric_dimensions_form_array.controls.reduce((accumulated_sum, dimension_control) => {
      const weight_percent = Number(dimension_control.get('weight_percent')?.value ?? 0);

      return accumulated_sum + (Number.isNaN(weight_percent) ? 0 : weight_percent);
    }, 0);

    this.rubric_weights_sum.set(weights_sum);
  }

  private updateRubricLevelsCount(): void {
    this.rubric_levels_count.set(this.rubric_levels_form_array.length);
  }

  private listenQuestionTypeChanges(): void {
    this.form.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((question_type): void => {
      if (question_type !== SkillQuestionTypeEnum.Text && this.options_form_array.length === 0) {
        this.addOption();
      }

      if (question_type === SkillQuestionTypeEnum.Text) {
        this.initializeDefaultRubricForTextQuestion();
      } else {
        this.clearRubricForm();
      }
    });
  }

  private loadSkills(): void {
    this.api_service
      .getSkills({ per_page: 500, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<Skill>): void => {
          this.skills.set(response.data);
        },
      });
  }

  private loadSkillQuestion(skill_question_id: number): void {
    this.loading_data.set(true);

    this.api_service
      .getSkillQuestion(skill_question_id, {
        with: 'skills,skill_question_options,skill_question_rubric.skill_question_rubric_dimensions.skill_question_rubric_dimension_levels,skill_question_rubric.skill_question_rubric_levels',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (skill_question: SkillQuestion): void => {
          this.form.patchValue({
            type: skill_question.type,
            body: skill_question.body,
            approximate_minutes: skill_question.approximate_minutes,
            answer_lines: skill_question.answer_lines,
            skill_ids: skill_question.skills?.map(skill => skill.id) ?? [],
          });

          this.options_form_array.clear();

          if (skill_question.type !== SkillQuestionTypeEnum.Text) {
            const skill_question_options = skill_question.skill_question_options ?? [];

            if (skill_question_options.length === 0) {
              this.addOption();
            } else {
              skill_question_options.forEach(skill_question_option => {
                this.options_form_array.push(
                  this.form_builder.group({
                    label: [skill_question_option.label, Validators.required],
                    is_correct: [skill_question_option.is_correct],
                  })
                );
              });
            }
          }

          this.populateRubricFromSkillQuestion(skill_question);
          this.updateRubricWeightsSum();
          this.loading_data.set(false);
        },
        error: (): void => {
          this.loading_data.set(false);
          this.snack_bar.open('No se pudo cargar la pregunta.', undefined, { duration: 5000 });
          void this.router.navigate(['/employees', 'rrhh', 'skill-questions']);
        },
      });
  }

  private populateRubricFromSkillQuestion(skill_question: SkillQuestion): void {
    this.rubric_dimensions_form_array.clear();
    this.rubric_levels_form_array.clear();
    this.rubric_rules.set([]);

    const skill_question_rubric = skill_question.skill_question_rubric;

    if (!skill_question_rubric || skill_question.type !== SkillQuestionTypeEnum.Text) {
      return;
    }

    const dimensions = [...(skill_question_rubric.skill_question_rubric_dimensions ?? [])];
    const levels = [...(skill_question_rubric.skill_question_rubric_levels ?? [])].sort(
      (first_level, second_level) => (first_level.sort_order ?? 0) - (second_level.sort_order ?? 0)
    );

    if (dimensions.length === 0 || levels.length === 0) {
      this.initializeDefaultRubricForTextQuestion();
      return;
    }

    dimensions.forEach(dimension => {
      this.rubric_dimensions_form_array.push(
        this.form_builder.group({
          name: [dimension.name, Validators.required],
          weight_percent: [dimension.weight_percent, [Validators.required, Validators.min(1), Validators.max(100)]],
        })
      );
    });

    levels.forEach(level => {
      this.rubric_levels_form_array.push(
        this.form_builder.group({
          name: [level.name, Validators.required],
          weight_percent: [level.weight_percent ?? 0, [Validators.required, Validators.min(0), Validators.max(100)]],
        })
      );
    });

    this.resizeRubricRulesMatrix();

    const level_id_to_index = new Map<number, number>();
    levels.forEach((level, level_index) => {
      if (level.id !== undefined) {
        level_id_to_index.set(level.id, level_index);
      }
    });

    dimensions.forEach((dimension, dimension_index) => {
      const dimension_levels = dimension.skill_question_rubric_dimension_levels ?? [];

      dimension_levels.forEach(dimension_level => {
        const level_index = level_id_to_index.get(dimension_level.skill_question_rubric_level_id);

        if (level_index === undefined) {
          return;
        }

        this.updateRubricRule(dimension_index, level_index, dimension_level.rules);
      });
    });

    this.bumpRubricMatrixRevision();
    this.updateRubricLevelsCount();
  }
}
